// PoiAutoHint v4 (M3, 2026-05-24 — persona-aware preset).
//
// History:
// v2: auto-activated 4 basic POI on zoom 12 — too noisy, dropped.
// v3: opt-in tip, generic 4-category set.
// v4 (M3): if the user's quiz answers map to a persona, propose the
//         persona-matching preset instead of the generic one.
//         family → schools/kindergartens/pharmacies/hospitals/supermarkets
//         nomad  → coworking/stations/airports/supermarkets/gym
//         sport  → ski_areas/ski_resorts/alpine_huts/pools/gym
//         quiet  → supermarkets/pharmacies/hospitals
//         urban  → stations/airports/language_schools/yoga/coworking
//         no persona → fall back to v3 generic basic set
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMapStore } from '@/features/map-core';
import { emit } from '@/lib/events';
import { usePoiStore } from './store';
import type { PoiCategory } from './types';
import { useQuizStore } from '@/features/quiz';
import { detectPersona, PERSONAS, type PersonaCode } from '@/features/recommender/persona';

const SESSION_KEY = 'relocation_poi_autohint_done_v1';
const TRIGGER_ZOOM = 12;
const BASIC_CATEGORIES: PoiCategory[] = ['hospitals', 'pharmacies', 'schools', 'stations'];

const PERSONA_PRESETS: Record<PersonaCode, PoiCategory[]> = {
  family: ['schools', 'kindergartens', 'pharmacies', 'hospitals', 'supermarkets'],
  nomad: ['coworking', 'stations', 'airports', 'supermarkets', 'gym'],
  sport: ['ski_areas', 'ski_resorts', 'alpine_huts', 'pools', 'gym'],
  quiet: ['supermarkets', 'pharmacies', 'hospitals'],
  urban: ['stations', 'airports', 'language_schools', 'yoga', 'coworking'],
};

export function PoiAutoHint() {
  const { t } = useTranslation();
  const zoom = useMapStore((s) => s.zoom);
  const setMany = usePoiStore((s) => s.setMany);
  const active = usePoiStore((s) => s.active);
  const answers = useQuizStore((s) => s.answers);
  const [visible, setVisible] = useState(false);

  const persona = useMemo(() => detectPersona(answers), [answers]);
  const preset = persona ? PERSONA_PRESETS[persona] : BASIC_CATEGORIES;

  useEffect(() => {
    if (visible) return;
    if (zoom < TRIGGER_ZOOM) return;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY)) return;
    if (active.size > 0) {
      sessionStorage?.setItem(SESSION_KEY, '1');
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- prompt is gated by zoom threshold + first-in-session; intentional one-shot toast
    setVisible(true);
  }, [zoom, active.size, visible]);

  const dismiss = () => {
    sessionStorage?.setItem(SESSION_KEY, '1');
    setVisible(false);
  };

  const handleEnable = () => {
    setMany(preset);
    dismiss();
  };

  const handleConfigure = () => {
    emit('poi:openPanel');
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-6 z-[1200] mx-auto flex max-w-[28rem] flex-col items-stretch gap-2 rounded-2xl border border-[color:var(--color-ink-100)] bg-[color:var(--color-paper)] px-4 py-3 shadow-lg sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:flex-row sm:items-center"
    >
      <span className="flex-1 text-sm text-[color:var(--color-ink-700)]">
        {persona
          ? t('poi.autoHint.personaPrompt', {
              defaultValue: 'Show POI tuned for your profile ({{label}})?',
              label: t(PERSONAS[persona].labelKey, { defaultValue: persona }),
            })
          : t('poi.autoHint.optInPrompt', {
              defaultValue:
                'Show essential infrastructure (hospitals, pharmacies, schools, stations) on the map?',
            })}
      </span>
      <div className="flex shrink-0 items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleEnable}
          className="rounded-full bg-[color:var(--color-terra-600)] px-3 py-1.5 text-xs font-semibold text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-terra-600)]"
        >
          {t('poi.autoHint.enable', { defaultValue: 'Show' })}
        </button>
        <button
          type="button"
          onClick={handleConfigure}
          className="rounded-full border border-[color:var(--color-ink-200)] px-3 py-1.5 text-xs font-semibold text-[color:var(--color-ink-700)] hover:bg-[color:var(--color-paper-2)]"
        >
          {t('poi.autoHint.configure', { defaultValue: 'Customise' })}
        </button>
        <button
          type="button"
          aria-label={t('poi.autoHint.dismiss', { defaultValue: 'Dismiss' })}
          onClick={dismiss}
          className="text-[color:var(--color-ink-400)] hover:text-[color:var(--color-ink-700)]"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
